import { useMemo, ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '../Button';
import { Appearance, Background, Design, Size } from '../designConstants';
import './BreadcrumbTitle.scss';

interface PathNode {
  uri?: string;
  label?: string;
  key?: string;
  resolvedPath?: string;
  // Runs instead of navigating, for crumbs that stand for page state rather than a route.
  onClick?: () => void;
}

interface BreadcrumbTitleProps {
  path: PathNode[];
  title: string;
  // Absolute path to navigate to for the title crumb and to prefix each trail crumb with. Falls
  // back to relative navigation (from the page's own matched route) when omitted, which only
  // resolves correctly for pages with no nested routing of their own.
  basePath?: string;
  // Runs instead of navigating when the title crumb is clicked.
  onTitleClick?: () => void;
}

export const BreadcrumbTitle = ({ path, title, basePath, onTitleClick }: BreadcrumbTitleProps) => {
  const navigate = useNavigate();

  const nodes = useMemo(() => {
    const crumbs: string[] = [];
    const nodeElements: ReactElement[] = [];

    for (const { uri, label, key, resolvedPath, onClick } of path) {
      crumbs.push(resolvedPath || uri || '');
      if (label) {
        const crumbPath = crumbs.join('/');
        nodeElements.push(
          <Button
            size={Size.LG}
            key={key}
            appearance={Appearance.EMPTY}
            design={Design.NEUTRAL}
            background={Background.DARK}
            className='crumb'
            onClick={() =>
              onClick ? onClick() : navigate(basePath ? `${basePath}/${crumbPath}` : crumbPath)
            }
          >
            {label}
          </Button>
        );
      }
    }

    return nodeElements;
  }, [path, navigate, basePath]);

  return (
    <div className='breadcrumb-title'>
      <Button
        size={Size.LG}
        appearance={Appearance.EMPTY}
        design={Design.NEUTRAL}
        background={Background.DARK}
        className='main-title'
        onClick={() => (onTitleClick ? onTitleClick() : navigate(basePath ?? './'))}
      >
        {title}
      </Button>
      {nodes}
    </div>
  );
};
